'use client';

import { c as _c } from "react/compiler-runtime";
import { cookies } from '@repo/utils';
import { useCallback, useEffect, useState } from 'react';
import { ANALYTICS_OPTED_OUT_COOKIE } from './session';
import { useSessionId } from './session-id-provider';

/**
 * Minimal PostHog surface required to synchronize analytics consent.
 */
export interface AnalyticsConsentPostHog {
  opt_in_capturing(): void;
  opt_out_capturing(): void;
}

/**
 * Create analytics-consent bindings for a specific storage key and PostHog client.
 */
export function createAnalyticsConsent({
  storageKey,
  posthog
}: {
  storageKey: string;
  posthog: AnalyticsConsentPostHog;
}) {
  function readAnalyticsEnabled(): boolean {
    try {
      const value = localStorage.getItem(storageKey);
      return value !== 'false'; // default opted-in
    } catch {
      return true;
    }
  }

  /**
   * Mirror the opted-out flag to a cookie so the server-side middleware
   * (`applySessionCorrelation`) can short-circuit session correlation for
   * opted-out users. Non-HttpOnly — client code also reads it for consistency
   * with the PostHog opt-out gating.
   */
  function writeAnalyticsCookie(enabled: boolean): void {
    if (typeof document === 'undefined') return;
    if (enabled) {
      cookies.remove(ANALYTICS_OPTED_OUT_COOKIE, {
        path: '/'
      });
    } else {
      cookies.set(ANALYTICS_OPTED_OUT_COOKIE, 'true', {
        expires: 365,
        // 1 year in days (js-cookie unit)
        path: '/',
        secure: true,
        sameSite: 'Lax'
      });
    }
  }
  function hasAnalyticsCookie(): boolean {
    if (typeof document === 'undefined') return false;
    return cookies.get(ANALYTICS_OPTED_OUT_COOKIE) !== undefined;
  }
  function writeAnalyticsEnabled(enabled: boolean): void {
    try {
      localStorage.setItem(storageKey, String(enabled));
    } catch {
      // localStorage unavailable (e.g. private browsing)
    }
    writeAnalyticsCookie(enabled);
  }

  /** Sync PostHog opt-in/out state with the user's preference. */
  function syncPostHog(enabled: boolean): void {
    try {
      if (enabled) {
        posthog.opt_in_capturing();
      } else {
        posthog.opt_out_capturing();
      }
    } catch {
      // PostHog may not be initialized
    }
  }

  /**
   * Manage analytics consent state for the current browser session.
   */
  function useAnalyticsConsent() {
    const $ = _c(5);
    const [isAnalyticsEnabled, setEnabled] = useState(_temp);
    let t0;
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = () => {
        const enabled = readAnalyticsEnabled();
        setEnabled(enabled);
        syncPostHog(enabled);
      };
      t1 = [];
      $[0] = t0;
      $[1] = t1;
    } else {
      t0 = $[0];
      t1 = $[1];
    }
    useEffect(t0, t1);
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = enabled_0 => {
        setEnabled(enabled_0);
        writeAnalyticsEnabled(enabled_0);
        syncPostHog(enabled_0);
      };
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const setAnalyticsEnabled = t2;
    let t3;
    if ($[3] !== isAnalyticsEnabled) {
      t3 = {
        isAnalyticsEnabled,
        setAnalyticsEnabled
      };
      $[3] = isAnalyticsEnabled;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }

  /**
   * Read analytics opt-out state from localStorage (non-React, for module-level
   * init).
   */
  function _temp() {
    return readAnalyticsEnabled();
  }
  function isAnalyticsOptedOut(): boolean {
    return !readAnalyticsEnabled();
  }

  /**
   * Forward-migrate pre-existing localStorage opt-outs to the
   * `mo-analytics-opted-out` cookie so the server-side proxy
   * (`applySessionCorrelation`) respects the user's preference on every request.
   *
   * Must run on every client bootstrap (e.g. from `instrumentation-client.ts`),
   * not from a component-scoped hook — users who opted out before this cookie
   * existed may never open the settings UI, and would otherwise be silently
   * re-enrolled in session correlation.
   *
   * No-ops when localStorage says opted-in or when the cookie is already set;
   * opting back in is handled by `setAnalyticsEnabled(true)` via the consent UI.
   */
  function syncAnalyticsOptOutCookie(): void {
    if (readAnalyticsEnabled()) return;
    if (hasAnalyticsCookie()) return;
    writeAnalyticsCookie(false);
  }
  return {
    useAnalyticsConsent,
    isAnalyticsOptedOut,
    syncAnalyticsOptOutCookie
  };
}

/**
 * Create a hook that syncs the server-minted session ID into the caller's
 * observability backend.
 */
export function createUseSessionIdSync(registerSessionId: (sessionId: string) => void) {
  /**
   * Syncs the server-minted session ID to PostHog as a super property so every
   * event (including session replays) carries `session_id`, enabling
   * cross-reference with Better Stack request logs.
   */
  function useSessionIdSync() {
    const $ = _c(3);
    const sessionId = useSessionId();
    let t0;
    let t1;
    if ($[0] !== sessionId) {
      t0 = () => {
        if (sessionId) {
          registerSessionId(sessionId);
        }
      };
      t1 = [sessionId];
      $[0] = sessionId;
      $[1] = t0;
      $[2] = t1;
    } else {
      t0 = $[1];
      t1 = $[2];
    }
    useEffect(t0, t1);
  }
  return useSessionIdSync;
}